import { useEffect, useState } from "react";
import { Plus, Star, Users, Pencil, Power, PowerOff } from "lucide-react";
import client, { extractError } from "@/api";

function PlanDialog({ existing, onClose, onSubmit }) {
  const [name, setName] = useState(existing?.name || "");
  const [fee, setFee] = useState(existing?.subscription_fee ?? 5000);
  const [multiplier, setMultiplier] = useState(existing?.credit_multiplier ?? 5);
  const [validity, setValidity] = useState(existing?.validity_days ?? 365);
  const [description, setDescription] = useState(existing?.description || "");
  const [recommended, setRecommended] = useState(existing?.is_recommended || false);
  const [order, setOrder] = useState(existing?.display_order ?? 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const initialLimit = Number(fee || 0) * Number(multiplier || 0);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      await onSubmit({
        name, subscription_fee: Number(fee), credit_multiplier: Number(multiplier),
        validity_days: Number(validity), description: description || null,
        is_recommended: recommended, display_order: Number(order),
      });
      onClose();
    } catch (err) {
      setError(extractError(err, "Unable to save plan."));
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog-backdrop" role="dialog" data-testid="plan-dialog">
      <form className="dialog wide" onSubmit={submit}>
        <h3>{existing ? "Edit plan" : "New subscription plan"}</h3>
        <div className="grid-2">
          <label>Plan name *<input required value={name} onChange={(e) => setName(e.target.value)} data-testid="plan-name-input" /></label>
          <label>Subscription fee (₹) *<input required type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="plan-fee-input" /></label>
          <label>Credit multiplier *<input required type="number" min="1" max="100" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} data-testid="plan-multiplier-input" /></label>
          <label>Validity (days) *<input required type="number" min="1" value={validity} onChange={(e) => setValidity(e.target.value)} data-testid="plan-validity-input" /></label>
          <label>Display order<input type="number" value={order} onChange={(e) => setOrder(e.target.value)} data-testid="plan-order-input" /></label>
          <label className="checkbox-row"><input type="checkbox" checked={recommended} onChange={(e) => setRecommended(e.target.checked)} data-testid="plan-recommended-input" />Mark as recommended</label>
        </div>
        <label>Description<textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="plan-description-input" /></label>
        <div className="calc-strip" data-testid="plan-initial-calc"><span>Initial credit limit</span><strong>₹{initialLimit.toLocaleString("en-IN")}</strong><small>Fee × Multiplier</small></div>
        {error && <div className="form-error">{error}</div>}
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onClose} data-testid="plan-dialog-cancel">Cancel</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="plan-dialog-submit">{busy ? "Saving…" : existing ? "Save changes" : "Create plan"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminSubscriptionPlansPage({ user }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const canEdit = ["SUPER_ADMIN", "OPERATIONS_ADMIN"].includes(user.role);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await client.get("/admin/subscription-plans?include_inactive=true");
      setPlans(res.data);
    } catch (err) {
      setError(extractError(err, "Unable to load plans."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (plan) => {
    try {
      await client.post(`/admin/subscription-plans/${plan.id}/${plan.is_active ? "deactivate" : "activate"}`);
      load();
    } catch (err) { setError(extractError(err)); }
  };

  const savePlan = async (payload) => {
    if (dialog?.existing) {
      await client.patch(`/admin/subscription-plans/${dialog.existing.id}`, payload);
    } else {
      await client.post("/admin/subscription-plans", payload);
    }
    load();
  };

  if (loading) return <div className="loading-block">Loading subscription plans…</div>;

  return (
    <div className="plans-page" data-testid="admin-plans-page">
      <div className="section-heading">
        <div><p className="eyebrow">SUBSCRIPTION CATALOGUE</p><h3>Plans and credit multipliers</h3></div>
        {canEdit && (
          <button type="button" className="primary-button" data-testid="new-plan-button" onClick={() => setDialog({ existing: null })}>
            <Plus size={14} /> New plan
          </button>
        )}
      </div>
      {error && <div className="form-error">{error}</div>}
      <table className="admin-table" data-testid="plans-table">
        <thead>
          <tr>
            <th>Plan</th><th>Fee</th><th>Multiplier</th><th>Initial credit</th><th>Validity</th><th>Subscribed</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {plans.length === 0 && <tr><td colSpan={8} className="empty-row">No plans yet — create one to get started.</td></tr>}
          {plans.map((p) => (
            <tr key={p.id} data-testid={`plan-row-${p.id}`}>
              <td>
                <strong>{p.name}</strong>{p.is_recommended && <span className="chip chip-info recommended-chip" data-testid={`plan-recommended-${p.id}`}><Star size={10} /> Recommended</span>}
                {p.description && <><br /><small className="muted">{p.description}</small></>}
              </td>
              <td>₹{p.subscription_fee.toLocaleString("en-IN")}</td>
              <td>{p.credit_multiplier}×</td>
              <td><strong>₹{p.initial_credit_limit.toLocaleString("en-IN")}</strong></td>
              <td>{p.validity_days} days</td>
              <td><Users size={12} /> {p.subscribed_count}</td>
              <td><span className={`status-chip status-${p.is_active ? "verified" : "suspended"}`}>{p.is_active ? "Active" : "Inactive"}</span></td>
              <td>
                {canEdit && (
                  <>
                    <button type="button" className="link-button" data-testid={`edit-plan-${p.id}`} onClick={() => setDialog({ existing: p })}><Pencil size={12} /> Edit</button>
                    <button type="button" className="link-button" data-testid={`toggle-plan-${p.id}`} onClick={() => toggleActive(p)}>
                      {p.is_active ? <><PowerOff size={12} /> Deactivate</> : <><Power size={12} /> Activate</>}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {dialog && <PlanDialog existing={dialog.existing} onClose={() => setDialog(null)} onSubmit={savePlan} />}
    </div>
  );
}
